import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from './store'
import { GetAppContextResponse } from '../types/sdk'

// add new pages here and in the `Pages.tsx` file
type Page =
  | 'connectToDrive'
  | 'disconnected'
  | 'error'
  | 'grantingDocAccess'
  | 'mobileClientUpgrade'
  | 'home'
  | 'joinCollaboration'
  | 'shareEmail'
  | 'initialLoading'
  | 'microsoftFileView'
  | 'microsoftFileViewCollab'

export type App = 'Microsoft' | 'Google'

type InitialState = {
  page: Page
  app?: App
  isGeneralSpinner: boolean
  appContext?: GetAppContextResponse['context']
  chat?: boolean
  notificationsAvailable?: boolean
  collaborationId?: string
  isNavigatingDuringCollab?: boolean
}

const initialState: InitialState = {
  page: 'initialLoading',
  isGeneralSpinner: false,
  isNavigatingDuringCollab: false,
}

const rootSlice = createSlice({
  name: 'root',
  initialState,
  reducers: {
    setPage: (state, { payload }: PayloadAction<Page>) => {
      state.page = payload
    },
    setApp: (state, { payload }: PayloadAction<App>) => {
      state.app = payload
    },
    setCollaborationId: (state, { payload }: PayloadAction<string>) => {
      state.collaborationId = payload
    },
    clearCollaborationId: (state) => {
      state.collaborationId = undefined
    },
    setIsGeneralSpinner: (state, { payload }: PayloadAction<boolean>) => {
      state.isGeneralSpinner = payload
    },
    setIsNavigatingDuringCollab: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isNavigatingDuringCollab = payload
    },
    setAppContext: (
      state,
      { payload }: PayloadAction<GetAppContextResponse>
    ) => {
      state.appContext = payload.context
    },
    setChat: (state, { payload }: PayloadAction<boolean>) => {
      state.chat = payload
    },
    setNotificationsAvailable: (state, { payload }: PayloadAction<boolean>) => {
      state.notificationsAvailable = payload
    },
  },
})

export const {
  setPage,
  setApp,
  setAppContext,
  setIsGeneralSpinner,
  setCollaborationId,
  clearCollaborationId,
  setChat,
  setNotificationsAvailable,
  setIsNavigatingDuringCollab,
} = rootSlice.actions

export const selectPage = (state: RootState) => state.root.page
export const selectApp = (state: RootState) => state.root.app
export const selectCollaborationId = (state: RootState) =>
  state.root.collaborationId
export const selectIsGeneralSpinner = (state: RootState) =>
  state.root.isGeneralSpinner
export const selectChat = (state: RootState) => state.root.chat
export const selectNotificationsAvailable = (state: RootState) =>
  state.root.notificationsAvailable
export const selectIsNavigatingDuringCollab = (state: RootState) =>
  state.root.isNavigatingDuringCollab

export default rootSlice
